// Accounting's lists as DataTable columns (BL-189 consolidation; were InvoiceTable and PaymentTable,
// pages/ux-full screens/accounting.js): what each column says, in words, with no drawing of its own.
// - Invoices (invoices.list): its number (the row's way in), the customer and job, when it was issued
//   and is due (how late, in red), its total, what's still owed, and where it stands as a word (Overdue
//   in red, Not sent yet and Part paid dashed under: waiting; DESIGN.md §2). A draft owes nothing yet,
//   and a void one nothing at all (QA-716).
// - Payments (payments.list): when it came in (the company's day and time), from whom and who took it,
//   what it paid (each invoice a link, credit kept, what was given back and why), how it was paid and
//   where it stands (To deposit dashed under; Disputed and Chargeback lost in red), and the amount, the
//   row's way in, struck through once voided.
import type { DataColumn, DataWord } from '../components/DataTable/DataTable.tsx';
import { clock, day, dayOf, money } from '../format.ts';
import type { InvoiceListRow, PaymentListProps } from '../money-schemas.ts';

export type MoneyWord = 'muted' | 'wait' | 'bad' | 'now';

/** Where an invoice stands, in words, drawn as the prototype draws it: red only when it's late. */
export function standingOf(r: Pick<InvoiceListRow, 'standing' | 'status'>): [string, MoneyWord] {
  switch (r.standing) {
    case 'draft':
      return ['Draft', 'muted'];
    case 'sent':
      return r.status === 'finalized' ? ['Not sent yet', 'wait'] : ['Sent', 'now'];
    case 'part_paid':
      return ['Part paid', 'wait'];
    case 'overdue':
      return ['Overdue', 'bad'];
    case 'paid':
      return ['Paid', 'muted'];
    case 'void':
      return ['Void', 'muted'];
  }
}

const tone = (k: MoneyWord): DataWord['tone'] => (k === 'now' ? 'ink' : k);

/** Accounting › Invoices' columns. */
export const invoiceColumns: readonly DataColumn<InvoiceListRow>[] = [
  { id: 'number', label: 'Invoice', rowHeader: true, way: true, cell: (r) => ({ text: r.number }) },
  {
    id: 'customer',
    label: 'Customer',
    cell: (r) => ({
      text: r.customerName,
      ...(r.jobNumber ? { lines: [{ text: `Job ${r.jobNumber}` }] } : {}),
    }),
  },
  {
    id: 'issued',
    label: 'Issued',
    cell: (r) => (r.issuedOn ? { text: day(r.issuedOn) } : { text: 'Not issued', look: 'muted' }),
  },
  {
    id: 'due',
    label: 'Due',
    cell: (r) => ({
      text: day(r.dueOn),
      ...(r.daysLate > 0
        ? {
            lines: [
              { text: r.daysLate === 1 ? '1 day late' : `${r.daysLate} days late`, tone: 'bad' as const },
            ],
          }
        : {}),
    }),
  },
  { id: 'total', label: 'Total', align: 'end', cell: (r) => ({ text: money(r.totalCents) }) },
  {
    id: 'owed',
    label: 'Owed',
    align: 'end',
    cell: (r) =>
      r.standing === 'draft'
        ? { text: 'Not yet', look: 'muted' }
        : { text: money(r.standing === 'void' ? 0 : Math.max(0, r.balanceCents)), look: 'strong' },
  },
  {
    id: 'standing',
    label: 'Where it stands',
    cell: (r) => {
      const [word, kind] = standingOf(r);
      return { word: { word, tone: tone(kind) } };
    },
  },
];

type PaymentRow = PaymentListProps['rows'][number];

const METHOD: Record<PaymentRow['method'], string> = {
  cash: 'Cash',
  cheque: 'Cheque',
  etransfer: 'E-transfer',
  card: 'Card',
  other: 'Other',
  financing: 'Financing',
};

const paymentWord = (p: PaymentRow): DataWord =>
  p.status === 'void'
    ? { word: 'Voided', tone: 'muted' }
    : p.status === 'disputed'
      ? { word: 'Disputed', tone: 'bad' }
      : p.status === 'chargeback_lost'
        ? { word: 'Chargeback lost', tone: 'bad' }
        : p.deposited
          ? { word: 'In the bank', tone: 'muted' }
          : { word: 'To deposit', tone: 'wait' };

/** Accounting › Payments and refunds' columns, in the company's zone; an invoice it paid opens where invoiceHrefOf says. */
export const paymentColumns = ({
  timeZone,
  invoiceHrefOf,
}: {
  timeZone: string;
  invoiceHrefOf?: (id: string) => string;
}): readonly DataColumn<PaymentRow>[] => [
  {
    id: 'when',
    label: 'When',
    rowHeader: true,
    cell: (p) => ({ text: dayOf(p.receivedAt, timeZone), lines: [{ text: clock(p.receivedAt, timeZone) }] }),
  },
  {
    id: 'customer',
    label: 'Customer',
    cell: (p) => ({
      text: p.customerName,
      ...(p.takenBy ? { lines: [{ text: `Taken by ${p.takenBy}` }] } : {}),
    }),
  },
  {
    id: 'for',
    label: 'For',
    cell: (p) => ({
      ...(p.invoices.length
        ? {
            links: p.invoices.map((i) => ({ id: i.id, text: i.number, href: invoiceHrefOf?.(i.id) ?? null })),
          }
        : { text: 'No invoice: credit', look: 'muted' as const }),
      lines: [
        ...(p.invoices.length > 0 && p.creditCents > 0
          ? [{ text: `${money(p.creditCents)} kept as credit` }]
          : []),
        ...(p.refundedCents > 0
          ? [{ text: `Refunded ${money(p.refundedCents)}${p.refundReason ? `: ${p.refundReason}` : ''}` }]
          : []),
      ],
    }),
  },
  { id: 'how', label: 'How', cell: (p) => ({ text: METHOD[p.method], lines: [paymentWord(p)] }) },
  {
    id: 'amount',
    label: 'Amount',
    align: 'end',
    way: true,
    cell: (p) => ({ text: money(p.amountCents), struck: p.status === 'void' }),
  },
];
